import React, { useEffect, useRef } from 'react';
import { gsap } from '../lib/gsap';
import { copy, Language } from '../content/i18n';

interface AboutProps {
  language: Language;
}

const About: React.FC<AboutProps> = ({ language }) => {
  const rootRef = useRef<HTMLElement>(null);
  const t = copy[language].about;

  useEffect(() => {
    const ctx = gsap.context(() => {
      const reveals = rootRef.current?.querySelectorAll('[data-reveal]') ?? [];

      gsap.set(reveals, { clearProps: 'opacity,transform' });

      reveals.forEach((el) => {
        gsap.fromTo(
          el,
          { opacity: 0, y: 28 },
          {
            opacity: 1,
            y: 0,
            duration: 1,
            ease: 'power3.out',
            immediateRender: false,
            scrollTrigger: {
              trigger: el,
              start: 'top 90%',
              toggleActions: 'play none none none',
            },
          }
        );
      });
    }, rootRef);

    return () => ctx.revert();
  }, [language]);

  return (
    <section
      ref={rootRef}
      id="story"
      style={{ background: '#080808', padding: 'clamp(72px,9vw,128px) clamp(24px,6vw,96px)' }}
    >
      <div style={{ maxWidth: 980, margin: '0 auto' }}>
        <p
          data-reveal
          style={{
            fontFamily: "'Montserrat', sans-serif",
            fontSize: '0.58rem',
            letterSpacing: '0.44em',
            color: '#c49428',
            textTransform: 'uppercase',
            fontWeight: 500,
            marginBottom: 18,
          }}
        >
          {t.heading}
        </p>

        <h2
          data-reveal
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: 'clamp(2.1rem, 4.8vw, 3.8rem)',
            fontWeight: 300,
            color: '#fff',
            letterSpacing: '0.05em',
            lineHeight: 1.12,
            margin: 0,
          }}
        >
          {t.title}
        </h2>

        <div
          data-reveal
          style={{
            width: 46,
            height: 1,
            background: 'rgba(196,148,40,0.45)',
            margin: '22px 0 0',
          }}
        />

        <blockquote
          data-reveal
          style={{
            margin: '26px 0 36px',
            maxWidth: 760,
            paddingLeft: 22,
            borderLeft: '2px solid #c49428',
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: 'clamp(1.3rem, 2vw, 1.65rem)',
            fontStyle: 'italic',
            fontWeight: 300,
            color: '#f0ebe3',
            lineHeight: 1.48,
          }}
        >
          {`\u201c${t.quote}\u201d`}
        </blockquote>

        <div style={{ maxWidth: 760, display: 'flex', flexDirection: 'column', gap: 20 }}>
          {t.paragraphs.map((paragraph, index) => (
            <p
              key={`about-paragraph-${index}`}
              data-reveal
              style={{
                margin: 0,
                fontFamily: "'Montserrat', sans-serif",
                fontSize: 'clamp(0.94rem, 1.1vw, 1.04rem)',
                lineHeight: 1.82,
                color: index === 0 ? '#cbc2b8' : '#b8b0a7',
                letterSpacing: '0.005em',
              }}
            >
              {paragraph}
            </p>
          ))}
        </div>
      </div>
    </section>
  );
};

export default About;
